import React, { useReducer } from 'react';

import _ from 'lodash';
import { Dialog, DialogContent, useTheme } from '@material-ui/core';

import API from 'utils/api';
import { useDialog, useSources } from 'hooks';
import { DialogActions, DialogTitle, Progress } from 'components';
import { Status } from './helpers';

import styles from './NextStageDialog.module.scss';

const reducer = (state, action) => {
  switch (action.type) {
    case 'INIT': {
      const props = action.payload;
      const status = _.get(props, 'state.status');

      const pending = _.pickBy({
        image: _.isEmpty(_.get(props, 'values.image')),
        date: !_.get(props, 'values.date'),
        tags: _.isEmpty(_.get(props, 'values.tags')),
      });

      return {
        id: _.get(props, 'state.id'),
        status,
        pending: _.isEmpty(pending) ? null : pending,
        policy: _.get(props, 'values.ticketPolicy'),
        prizes: _.get(props, 'values.numPrizes'),
        step: status !== Status.idle || _.isEmpty(pending) ? 1 : 0,
      };
    }

    case 'STEP':
      return action.payload !== state.step ? { ...state, step: action.payload } : state;

    default:
      return state;
  }
};

const NextStageDialog = props => {
  const [state, dispatch] = useReducer(reducer, {});

  const theme = useTheme();
  const sources = useSources();

  const { isOpening } = useDialog(props.open, {
    onOpen: () => dispatch({ type: 'INIT', payload: props }),
    onClose: () => sources.cancelAll(),
  });

  if (isOpening || _.isEmpty(state)) {
    return null;
  }

  const { step, status } = state;

  const promote = async () => {
    dispatch({ type: 'STEP', payload: 2 });

    const { data, error } = await (status === Status.closed
      ? API.runDraw
      : API.promoteDraw)(state.id, sources);

    props.dispatch(
      error
        ? { type: 'ERROR', payload: error }
        : status === Status.closed
        ? {
            type: 'RESULTS',
            payload: { results: data.results, status: Status[data.status] },
          }
        : { type: 'STATUS', payload: Status[data] }
    );
  };

  const renderContent = () => {
    const highlight = value => (
      <span style={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
        {value}
      </span>
    );

    switch (status) {
      case Status.idle: {
        if (step) {
          const policy = {
            once: ['once', <>a maximum of {highlight('one ticket')} for this draw</>],
            onceTag: [
              'once per tag',
              <>a ticket for {highlight('each tag')} defined for this draw</>,
            ],
            unlimited: [
              'unlimited',
              <>
                a ticket {highlight('every time')} they acquire any tag defined in this
                draw
              </>,
            ],
          }[state.policy];

          return (
            <>
              <p>Once opened, users will be able to receive tickets to participate in.</p>
              <p>
                The ticket policy is currently set to {highlight(_.toUpper(policy[0]))}{' '}
                and users will receive {policy[1]}. This setting cannot be changed later.
              </p>
              <p>Do you want to proceed?</p>
            </>
          );
        }

        const translate = { date: 'Draw date' };
        const pending = _.keys(state.pending).map(it => (
          <li key={it}>{translate[it] || _.capitalize(it)}</li>
        ));

        return (
          <>
            <p>
              The following field{pending.length > 1 ? 's have' : ' has'} not been
              defined:
            </p>

            <ul>{pending}</ul>

            <p>
              You can proceed to the next stage, but it is highly recommended that{' '}
              {pending.length > 1 ? 'these fields' : 'this field'} be filled in, as the
              draw will be visible to users from now on.
            </p>
            <p>Do you want to continue?</p>
          </>
        );
      }

      case Status.open:
        return (
          <>
            <p>
              This will end the ticket acquisition period. Make sure there are{' '}
              {highlight('enough tickets')} for the draw to take place.
            </p>

            <p>Do you want to proceed?</p>
          </>
        );

      case Status.closed:
        return (
          <>
            <h3>The big moment has finally arrived!</h3>

            {state.prizes === 1 ? (
              <p>
                Only {highlight('ONE PRIZE')} will be drawn. Click the{' '}
                {highlight('FIND WINNER')} button bellow to meet the lucky one.
              </p>
            ) : (
              <p>
                A total of {highlight(`${state.prizes} PRIZES`)} will be drawn. Click the{' '}
                {highlight('FIND WINNERS')} button bellow to meet the lucky ones.
              </p>
            )}
          </>
        );

      case Status.redemption:
        return (
          <>
            <p>
              This will permanently end the draw. Make sure the results have been{' '}
              {highlight('published')} and all prizes {highlight('redeemed')}.
            </p>
            <p>Do you want to proceed?</p>
          </>
        );

      default:
        return null;
    }
  };

  const getActions = () => {
    switch (status) {
      case Status.idle:
        return [
          { label: 'no, cancel', onClick: props.onClose },
          {
            label: step ? 'yes, open' : "yes, i'm aware",
            onClick: () => (step ? promote() : dispatch({ type: 'STEP', payload: 1 })),
            variant: 'contained',
          },
        ];

      case Status.open:
        return [
          { label: 'no, cancel', onClick: props.onClose },
          { label: 'yes, close', onClick: promote, variant: 'contained' },
        ];

      case Status.closed:
        return [
          { label: 'maybe later', onClick: props.onClose },
          {
            label: `find winner${state.prizes > 1 ? 's' : ''}`,
            onClick: promote,
            variant: 'contained',
          },
        ];

      case Status.redemption:
        return [
          { label: 'no, cancel', onClick: props.onClose },
          { label: 'yes, please', onClick: promote, variant: 'contained' },
        ];

      default:
        return [];
    }
  };

  return (
    <Dialog open={props.open} classes={{ paper: styles.dialog }}>
      <DialogTitle>{step ? 'Next Stage Confirmation' : 'Warning'}</DialogTitle>

      <div
        className={styles.wrapper}
        style={step === 2 ? { visibility: 'hidden' } : undefined}
      >
        <DialogContent className={styles.content}>{renderContent()} </DialogContent>
        <DialogActions actions={getActions()} />
      </div>

      {step === 2 ? (
        <div className={styles.loader}>
          <Progress />
        </div>
      ) : null}
    </Dialog>
  );
};

export default NextStageDialog;
